import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardCheck, faArrowsRotate, faXmark, faCircleCheck,
  faMagnifyingGlass, faUser, faLink, faIdCard, faDoorOpen,
  faCircleXmark, faUpload, faTriangleExclamation, faPrint,
  faChevronDown, faChevronUp, faBed,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "./PreCheckinPanel.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";
const FRONTEND = window.location.origin;

interface RoomEntry {
  roomId: string | null;
  roomNumber: number | null;
  roomType: string | null;
  roomReady: boolean;
  roomStatus: string;
  guestName: string | null;
  guestIndex: number;
  totalPrice: number;
  passportSubmitted: boolean;
}

interface BookingGroup {
  bookingId: string;
  guestName: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  totalPrice: number;
  phone: string;
  status: string;
  rooms: RoomEntry[];
}

interface ImageState {
  file: File | null;
  preview: string | null;
  path: string | null;
  uploading: boolean;
}

const EMPTY_IMG: ImageState = { file: null, preview: null, path: null, uploading: false };

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function today() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}

export default function PreCheckinPanel() {
  const [bookings, setBookings] = useState<BookingGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [checkingIn, setCheckingIn] = useState<string | null>(null);
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());

  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Modal state
  const [modalBookingId, setModalBookingId] = useState<string | null>(null);
  const [modalGuestIndex, setModalGuestIndex] = useState(0);
  const [modalGuestName, setModalGuestName] = useState("");
  const [loadingPassport, setLoadingPassport] = useState(false);
  const [savingPassport, setSavingPassport] = useState(false);
  const [docType, setDocType] = useState<"national_id" | "passport">("national_id");
  const [frontImg, setFrontImg] = useState<ImageState>(EMPTY_IMG);
  const [backImg, setBackImg] = useState<ImageState>(EMPTY_IMG);
  const [existingRecord, setExistingRecord] = useState<{ image_front?: string; image_back?: string; doc_type?: string } | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/pre-checkin/list`, { headers });
      const list: BookingGroup[] = data || [];
      setBookings(list);
      // auto-expand all by default
      setExpandedSet(new Set(list.map(b => b.bookingId)));
    } catch {
      showToast("Failed to load upcoming arrivals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id: string) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const copyLink = (bookingId: string) => {
    const link = `${FRONTEND}/pre-checkin?booking=${encodeURIComponent(bookingId)}`;
    navigator.clipboard.writeText(link);
    setCopiedId(bookingId);
    setTimeout(() => setCopiedId(null), 2000);
    showToast("Link copied to clipboard");
  };

  const openModal = async (bookingId: string, guestIndex: number, guestName: string) => {
    setModalBookingId(bookingId);
    setModalGuestIndex(guestIndex);
    setModalGuestName(guestName);
    setDocType("national_id");
    setFrontImg(EMPTY_IMG);
    setBackImg(EMPTY_IMG);
    setExistingRecord(null);
    setLoadingPassport(true);
    try {
      const { data } = await axios.get(`${API}/api/passports/${encodeURIComponent(bookingId)}`, { headers });
      const records = Array.isArray(data) ? data : [];
      const match = records.find((r: any) => r.guest_index === guestIndex) ?? null;
      if (match) {
        setExistingRecord(match);
        if (match.doc_type === "Passport") setDocType("passport");
      }
    } catch { /* no existing */ }
    finally { setLoadingPassport(false); }
  };

  const closeModal = () => { setModalBookingId(null); setFrontImg(EMPTY_IMG); setBackImg(EMPTY_IMG); setExistingRecord(null); };

  const pickFile = (side: "front" | "back", file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const upd = { file, preview: e.target?.result as string, path: null, uploading: false };
      side === "front" ? setFrontImg(upd) : setBackImg(upd);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (side: "front" | "back") => {
    if (!modalBookingId) return;
    const state = side === "front" ? frontImg : backImg;
    if (!state.file) return;
    const setState = side === "front" ? setFrontImg : setBackImg;
    setState(s => ({ ...s, uploading: true }));
    try {
      const form = new FormData();
      form.append("image", state.file);
      form.append("bookingId", modalBookingId);
      form.append("guestIndex", String(modalGuestIndex));
      form.append("side", side);
      form.append("guestName", modalGuestName);
      const res = await axios.post(`${API}/api/passports/upload`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setState(s => ({ ...s, path: res.data.imagePath, uploading: false }));
    } catch {
      showToast("Upload failed — try again");
      setState(s => ({ ...s, uploading: false }));
    }
  };

  const savePassport = async () => {
    if (!modalBookingId) return;
    const canSave = docType === "passport" ? !!frontImg.path : !!(frontImg.path && backImg.path);
    if (!canSave) { showToast("Upload the required ID image(s) first"); return; }
    setSavingPassport(true);
    try {
      await axios.post(`${API}/api/passports`, {
        reservationno: modalBookingId,
        guest_index: modalGuestIndex,
        guest_name: modalGuestName,
        doc_type: docType === "national_id" ? "National ID" : "Passport",
        image_front: frontImg.path || null,
        image_back: backImg.path || null,
      }, { headers });
      showToast("ID saved successfully");
      setBookings(prev => prev.map(b => {
        if (b.bookingId !== modalBookingId) return b;
        return { ...b, rooms: b.rooms.map(r => r.guestIndex === modalGuestIndex ? { ...r, passportSubmitted: true } : r) };
      }));
      closeModal();
    } catch { showToast("Failed to save ID record"); }
    finally { setSavingPassport(false); }
  };

  const handleCheckIn = async (bookingId: string) => {
    setCheckingIn(bookingId);
    try {
      await axios.put(`${API}/api/dashboard/checkin/${bookingId}`, {}, { headers });
      setBookings(prev => prev.filter(b => b.bookingId !== bookingId));
      showToast("Guest checked in successfully");
    } catch { showToast("Check-in failed — try from Check In/Out page"); }
    finally { setCheckingIn(null); }
  };

  const td = today();
  const filtered = bookings.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return b.guestName.toLowerCase().includes(q) || b.bookingId.toLowerCase().includes(q);
  });

  const todayArrivals    = filtered.filter(b => b.checkIn === td);
  const upcomingArrivals = filtered.filter(b => b.checkIn > td);
  const totalIdSubmitted = bookings.reduce((a, b) => a + b.rooms.filter(r => r.passportSubmitted).length, 0);
  const totalMissing     = bookings.reduce((a, b) => a + b.rooms.filter(r => !r.passportSubmitted).length, 0);
  const canSave = docType === "passport" ? !!frontImg.path : !!(frontImg.path && backImg.path);

  const ImageBox = ({ side, label }: { side: "front" | "back"; label: string }) => {
    const state = side === "front" ? frontImg : backImg;
    const inputId = `pcp-img-${modalBookingId}-${modalGuestIndex}-${side}`;
    return (
      <div style={{ flex: 1 }}>
        <div className="pcp-pf-label" style={{ marginBottom: 6 }}>{label}</div>
        <div
          className={`pcp-img-drop${state.path ? " uploaded" : ""}`}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {state.preview
            ? <img src={state.preview} alt={side} style={{ maxHeight: 90, maxWidth: "100%", objectFit: "contain", borderRadius: 6 }} />
            : <span className="pcp-img-placeholder"><FontAwesomeIcon icon={faUpload} /> Click to choose</span>
          }
          <input id={inputId} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
            onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(side, f); }} />
        </div>
        {state.file && !state.path && (
          <button className="pcp-upload-btn" onClick={() => uploadImage(side)} disabled={state.uploading}>
            {state.uploading ? "Uploading…" : "Upload"}
          </button>
        )}
        {state.path && <div className="pcp-upload-done"><FontAwesomeIcon icon={faCircleCheck} /> Uploaded</div>}
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="pcp-page">
        <div className="pcp-header">
          <div>
            <h1 className="pcp-title"><FontAwesomeIcon icon={faClipboardCheck} /> Digital Pre Check-in</h1>
            <p className="pcp-subtitle">Send links to guests · review submitted ID data · manually upload ID photos</p>
          </div>
          <button className="pcp-btn-refresh" onClick={load}><FontAwesomeIcon icon={faArrowsRotate} /> Refresh</button>
        </div>

        <div className="pcp-status-bar">
          <div className="pcp-status-pill pill-green">
            <span className="pcp-pill-count">{totalIdSubmitted}</span> ID Submitted
          </div>
          <div className="pcp-status-pill pill-amber">
            <span className="pcp-pill-count">{totalMissing}</span> Missing ID
          </div>
          <div className="pcp-status-pill pill-gold">
            <span className="pcp-pill-count">{todayArrivals.length}</span> Arriving Today
          </div>
        </div>

        <div className="pcp-search-wrap">
          <FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: "rgba(242,235,217,0.4)" }} />
          <input className="pcp-search" placeholder="Search guest or booking ID…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {loading ? (
          <div className="pcp-loading">Loading upcoming arrivals…</div>
        ) : bookings.length === 0 ? (
          <div className="pcp-empty">
            <FontAwesomeIcon icon={faCircleCheck} /><p>No upcoming arrivals in the next 14 days</p>
          </div>
        ) : (
          <>
            {todayArrivals.length > 0 && (
              <>
                <div className="pcp-section-label arriving-today">Arriving Today</div>
                {todayArrivals.map(b => (
                  <BookingCard key={b.bookingId} booking={b} isToday
                    copiedId={copiedId} onCopy={copyLink} onOpenModal={openModal}
                    checkingIn={checkingIn} onCheckIn={handleCheckIn}
                    expanded={expandedSet.has(b.bookingId)} onToggle={() => toggleExpand(b.bookingId)}
                  />
                ))}
              </>
            )}
            {upcomingArrivals.length > 0 && (
              <>
                <div className="pcp-section-label" style={{ marginTop: todayArrivals.length > 0 ? 28 : 0 }}>
                  Upcoming · Next 14 Days
                </div>
                {upcomingArrivals.map(b => (
                  <BookingCard key={b.bookingId} booking={b} isToday={false}
                    copiedId={copiedId} onCopy={copyLink} onOpenModal={openModal}
                    checkingIn={null} onCheckIn={() => {}}
                    expanded={expandedSet.has(b.bookingId)} onToggle={() => toggleExpand(b.bookingId)}
                  />
                ))}
              </>
            )}
            {filtered.length === 0 && search && (
              <div className="pcp-empty"><p>No results for "{search}"</p></div>
            )}
          </>
        )}

        {/* ID Upload Modal */}
        {modalBookingId && (
          <div className="pcp-modal-overlay" onClick={closeModal}>
            <div className="pcp-modal" onClick={e => e.stopPropagation()}>
              <div className="pcp-modal-header">
                <h3>
                  <FontAwesomeIcon icon={faIdCard} />
                  {modalGuestName || "Guest"}
                  <span className="pcp-modal-booking-id">{modalBookingId}</span>
                </h3>
                <button className="pcp-modal-close" onClick={closeModal}><FontAwesomeIcon icon={faXmark} /></button>
              </div>
              <div className="pcp-modal-body">
                {loadingPassport ? (
                  <div className="pcp-modal-loading">Loading…</div>
                ) : (
                  <>
                    {existingRecord && (existingRecord.image_front || existingRecord.image_back) && (
                      <div className="pcp-existing-record">
                        <FontAwesomeIcon icon={faCircleCheck} /> ID images already on file — upload new ones to replace
                      </div>
                    )}
                    <div style={{ marginBottom: 16 }}>
                      <div className="pcp-pf-label" style={{ marginBottom: 6 }}>Document Type</div>
                      <select className="pcp-select" value={docType} onChange={e => {
                        setDocType(e.target.value as "national_id" | "passport");
                        setFrontImg(EMPTY_IMG); setBackImg(EMPTY_IMG);
                      }}>
                        <option value="national_id">National ID (front + back)</option>
                        <option value="passport">Passport (single page)</option>
                      </select>
                    </div>
                    <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                      <ImageBox side="front" label={docType === "national_id" ? "Front Side" : "Passport Page"} />
                      {docType === "national_id" && <ImageBox side="back" label="Back Side" />}
                    </div>
                    {!canSave && (
                      <div className="pcp-hint">
                        <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#f59e0b" }} />
                        {docType === "national_id" ? "Upload both sides, then click Save" : "Upload the passport page, then click Save"}
                      </div>
                    )}
                    <div className="pcp-modal-actions">
                      <button className="pcp-btn-approve" onClick={savePassport} disabled={savingPassport || !canSave} style={{ opacity: canSave ? 1 : 0.5 }}>
                        <FontAwesomeIcon icon={faIdCard} /> {savingPassport ? "Saving…" : "Save ID Record"}
                      </button>
                      <button className="pcp-btn-cancel" onClick={closeModal}>Cancel</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {toast && <div className="pcp-toast">{toast}</div>}
      </div>
    </DashboardLayout>
  );
}

function BookingCard({
  booking, isToday, copiedId, onCopy, onOpenModal, checkingIn, onCheckIn, expanded, onToggle,
}: {
  booking: BookingGroup;
  isToday: boolean;
  copiedId: string | null;
  onCopy: (id: string) => void;
  onOpenModal: (bookingId: string, guestIndex: number, guestName: string) => void;
  checkingIn: string | null;
  onCheckIn: (id: string) => void;
  expanded: boolean;
  onToggle: () => void;
}) {
  const allSubmitted = booking.rooms.length > 0 && booking.rooms.every(r => r.passportSubmitted);
  const allReady     = booking.rooms.length > 0 && booking.rooms.every(r => r.roomReady);
  const totalAmount  = booking.totalPrice || booking.rooms.reduce((a, r) => a + (r.totalPrice || 0), 0);
  const firstType    = booking.rooms[0]?.roomType || null;

  // Group guests by room so multi-guest rooms render as one block
  const roomGroups: { roomId: string; roomNumber: number | null; roomType: string | null; roomReady: boolean; guests: RoomEntry[] }[] = [];
  const seen = new Map<string, number>();
  for (const r of booking.rooms) {
    const key = r.roomId ?? `g${r.guestIndex}`;
    if (!seen.has(key)) {
      seen.set(key, roomGroups.length);
      roomGroups.push({ roomId: key, roomNumber: r.roomNumber, roomType: r.roomType, roomReady: r.roomReady, guests: [] });
    }
    roomGroups[seen.get(key)!].guests.push(r);
  }

  return (
    <div className="pcp-card">
      {/* ── Top: identity + stats ── */}
      <div className="pcp-card-top">
        <div className="pcp-card-identity">
          <div className="pcp-avatar">{(booking.guestName || "?")[0].toUpperCase()}</div>
          <div>
            <div className="pcp-card-name-row">
              <span className="pcp-card-name">{booking.guestName || "—"}</span>
              {firstType && <span className="pcp-tier-badge">{firstType}</span>}
            </div>
            <div className="pcp-card-bookingid">Booking · {booking.bookingId}</div>
          </div>
        </div>
        <div className="pcp-card-stats">
          <div className="pcp-stat"><span className="pcp-stat-label">CHECK-IN</span><span className="pcp-stat-value">{fmt(booking.checkIn)}</span></div>
          <div className="pcp-stat"><span className="pcp-stat-label">NIGHTS</span><span className="pcp-stat-value">{booking.nights ?? "—"}</span></div>
          <div className="pcp-stat"><span className="pcp-stat-label">TOTAL</span><span className="pcp-stat-value">EGP {totalAmount ? totalAmount.toLocaleString() : "—"}</span></div>
        </div>
      </div>

      {/* ── Bar: status dots + actions ── */}
      <div className="pcp-card-bar">
        <div className="pcp-status-dots">
          <span className="pcp-dot-item">
            <span className={`pcp-dot ${allReady ? "green" : "red"}`} />
            {allReady ? "Room ready" : "Room not ready"}
          </span>
          <span className="pcp-dot-sep">·</span>
          <span className="pcp-dot-item">
            <span className={`pcp-dot ${allSubmitted ? "green" : "red"}`} />
            {allSubmitted ? "ID submitted" : "ID missing"}
          </span>
        </div>
        <div className="pcp-bar-actions">
          <button className="pcp-btn pcp-btn-ghost" onClick={() => onCopy(booking.bookingId)}>
            {copiedId === booking.bookingId ? "Copied!" : "Copy link"}
          </button>
          <button className="pcp-btn pcp-btn-secondary" onClick={() => {}}>Print RC</button>
          <button className="pcp-btn pcp-btn-secondary" onClick={onToggle}>
            Rooms ({booking.rooms.length})
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="10" height="10" style={{ marginLeft: 2 }}>
              {expanded ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
            </svg>
          </button>
          {isToday && (
            <button className="pcp-btn pcp-btn-primary" disabled={checkingIn === booking.bookingId} onClick={() => onCheckIn(booking.bookingId)}>
              {checkingIn === booking.bookingId ? "Checking in…" : "Check in all"}
            </button>
          )}
        </div>
      </div>

      {/* ── Room blocks ── */}
      {expanded && roomGroups.length > 0 && (
        <div className="pcp-rooms-list">
          {roomGroups.map((rg, ri) => (
            <div key={rg.roomId} className={`pcp-room-block${rg.roomReady ? " ready" : ""}`}>
              <div className="pcp-room-header">
                <div className="pcp-room-header-left">
                  <span className="pcp-room-label">Room {rg.roomNumber || ri + 1}</span>
                  {rg.roomType && <span className="pcp-room-type">{rg.roomType}</span>}
                </div>
                <button className="pcp-btn pcp-btn-checkin-sm" onClick={() => onCheckIn(booking.bookingId)}>Check in</button>
              </div>
              {rg.guests.map((guest, gi) => (
                <div key={gi} className="pcp-guest-row">
                  <span className={`pcp-dot ${guest.passportSubmitted ? "green" : "red"}`} />
                  <span className="pcp-guest-name">{guest.guestName || `Guest ${gi + 1}`}</span>
                  {gi === 0 && <span className="pcp-guest-role">Main guest</span>}
                  <button
                    className="pcp-btn pcp-btn-upload"
                    onClick={() => onOpenModal(booking.bookingId, guest.guestIndex, guest.guestName || `Guest ${gi + 1}`)}
                  >
                    {guest.passportSubmitted ? "View ID" : "Upload ID"}
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {booking.rooms.length === 0 && (
        <div className="pcp-no-rooms">No rooms assigned yet</div>
      )}
    </div>
  );
}
